import React from 'react';
import { useTranslation } from 'react-i18next';
import type { HotelRoomOption, SortedRate } from '../types/hotels.presentation';
import type { RatePolicyMode } from '../hooks/useHotelBooking';
import { formatHotelPrice } from '../utils/formatHotelPrice';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import {
  LISTING_PRIMARY_BUTTON_CLASS,
  listingPrimaryButtonStyle,
} from 'legacyfrontend/hooks/listingPrimaryButton';

const ROOM_OPTION_FALLBACK_KEY = 'hotels.detail.roomOptionFallback';

interface RoomOptionCardProps {
  option: HotelRoomOption;
  accommodationLabel?: string;
  onBook?: (rateKey: string, rateName?: string) => void;
  canBook?: boolean;
  /** Availability-based policy mode per rate key. Search `refundable` is not trusted. */
  ratePolicyModes?: Record<string, RatePolicyMode>;
  policyQuotesReady?: boolean;
}

function toDisplayText(value: unknown) {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function normalizeText(value: unknown) {
  return toDisplayText(value)?.toLowerCase().replace(/\s+/g, '');
}

function isRefundabilityDuplicate(value: unknown, refundable: boolean) {
  const normalized = normalizeText(value);
  if (!normalized) return false;

  const duplicateLabels = refundable
    ? ['refundable', 'reembolsable']
    : ['nonrefundable', 'norefundable', 'noreembolsable', 'nonreembolsable'];

  return duplicateLabels.includes(normalized);
}

function RateRow({
  rate,
  isBest,
  currency,
  accommodationLabel,
  onBook,
  canBook,
  policyMode,
  policyQuotesReady,
}: {
  rate: SortedRate;
  isBest: boolean;
  currency: string;
  accommodationLabel?: string;
  onBook?: (rateKey: string, rateName?: string) => void;
  canBook?: boolean;
  policyMode?: RatePolicyMode;
  policyQuotesReady?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { background: listingBackground, text: listingText } = useListingColors();

  const displayBoard = rate.meal || rate.board;
  const roomCount = (rate.rooms ?? []).reduce((total, room) => total + room.units, 0);
  // Missing/blocked policy (`unavailable`) uses the same Consultar UX as NR inquiry.
  const isInquiry = policyMode === 'inquiry' || policyMode === 'unavailable';
  const isBookable = policyMode === 'bookable';
  const quotesReady = !!policyQuotesReady;
  const showPolicyBadge = quotesReady && (isInquiry || isBookable);

  const cancellationText =
    rate.refundable != null && isRefundabilityDuplicate(rate.cancellation, rate.refundable)
      ? undefined
      : toDisplayText(rate.cancellation);
  const remarksText =
    rate.refundable != null && isRefundabilityDuplicate(rate.remarks, rate.refundable)
      ? undefined
      : toDisplayText(rate.remarks);

  const hasBodyRows =
    displayBoard != null ||
    showPolicyBadge ||
    accommodationLabel != null ||
    rate.promotionLabel != null ||
    remarksText != null;

  const bookEnabled =
    !!canBook &&
    // While quotes load, keep buttons disabled. Afterwards enable any rate with a
    // known disposition, or without one yet so click can fetch availability on demand.
    (quotesReady ? isInquiry || isBookable || policyMode == null : false);

  return (
    <div
      className={`rounded-lg border ${
        isBest ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'
      }`}
    >
      <div className='flex items-start justify-between gap-x-3 px-3 pt-3'>
        <h4 className='text-sm font-semibold text-gray-900 leading-snug'>
          {rate.name || t('hotels.detail.roomOptionFallback')}
        </h4>
        <div className='flex shrink-0 flex-col items-end gap-0.5'>
          {isBest ? (
            <span className='rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800'>
              {t('hotels.detail.bestPrice')}
            </span>
          ) : null}
          <span className='text-sm font-bold text-gray-900'>
            {formatHotelPrice(rate.sale, rate.currency ?? currency, i18n.language) ??
              t('hotels.results.rateUnavailable')}
          </span>
          {roomCount > 1 ? (
            <span className='text-xs text-gray-500'>
              {t('hotels.detail.totalForRooms', { count: roomCount })}
            </span>
          ) : null}
        </div>
      </div>

      {/* Structured body rows */}
      {hasBodyRows ? (
        <div className='mt-2 space-y-1 px-3 pb-3'>
          {displayBoard ? (
            <p className='flex flex-wrap items-center gap-x-1.5 text-xs'>
              <span className='text-gray-500'>{t('hotels.detail.board')}: </span>
              <span className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 ring-1 ring-inset ring-gray-200 text-gray-700'>
                {displayBoard}
                {rate.mealType ? ` (${rate.mealType})` : null}
              </span>
            </p>
          ) : null}

          {showPolicyBadge ? (
            <p className='flex flex-wrap items-center gap-x-1.5 text-xs'>
              <span className='text-gray-500'>{t('hotels.detail.cancellation')}: </span>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                  isBookable
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-amber-50 text-amber-700 ring-amber-200'
                }`}
              >
                {isBookable ? t('hotels.detail.refundable') : t('hotels.detail.nonRefundable')}
              </span>
              {cancellationText ? <span className='text-gray-500'>{cancellationText}</span> : null}
            </p>
          ) : null}

          {accommodationLabel ? (
            <p className='flex flex-wrap items-center gap-x-1.5 text-xs'>
              <span className='text-gray-500'>{t('hotels.card.accommodationType')}: </span>
              <span className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 ring-1 ring-inset ring-gray-200 text-gray-700'>
                {accommodationLabel}
              </span>
            </p>
          ) : null}

          {rate.promotionLabel ? (
            <p className='flex flex-wrap items-center gap-x-1.5 text-xs'>
              <span className='inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 ring-1 ring-inset ring-orange-200'>
                {rate.promotionLabel}
              </span>
            </p>
          ) : null}

          {remarksText ? <p className='text-xs italic text-gray-400'>{remarksText}</p> : null}
        </div>
      ) : (
        <div className='pb-3' />
      )}

      {canBook ? (
        <div className='border-t border-gray-100 px-3 pb-3 pt-2'>
          {isInquiry ? (
            <p className='mb-2 text-xs text-amber-700'>{t('hotels.booking.nonRefundableNote')}</p>
          ) : null}
          <button
            className={`w-full rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 ${LISTING_PRIMARY_BUTTON_CLASS}`}
            disabled={!bookEnabled}
            onClick={() => onBook?.(rate.key, rate.name)}
            style={listingPrimaryButtonStyle(listingBackground, listingText)}
            type='button'
          >
            {!quotesReady
              ? t('hotels.booking.checking')
              : isInquiry
                ? t('hotels.booking.inquire')
                : t('hotels.booking.reserve')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function RoomOptionCard({
  option,
  accommodationLabel,
  onBook,
  canBook,
  ratePolicyModes,
  policyQuotesReady,
}: RoomOptionCardProps) {
  const { t } = useTranslation();

  const roomTypeLabel =
    option.type === ROOM_OPTION_FALLBACK_KEY ? t('hotels.detail.roomOptionFallback') : option.type;

  return (
    <article className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
      <div className='border-b border-gray-100 px-4 py-3'>
        <h3 className='text-base font-semibold text-gray-900'>{roomTypeLabel}</h3>
      </div>

      <div className='space-y-2 p-3'>
        {option.rates.map((rate, index) => (
          <RateRow
            accommodationLabel={accommodationLabel}
            canBook={canBook}
            currency={option.currency}
            isBest={index === 0}
            key={rate.key}
            onBook={onBook}
            policyMode={ratePolicyModes?.[rate.key]}
            policyQuotesReady={policyQuotesReady}
            rate={rate}
          />
        ))}
      </div>
    </article>
  );
}
